import { useId, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  Download,
  WandSparkles,
} from "lucide-react";
import { parseJson, type JsonSyntaxProblem } from "./diagnostics";

export interface JsonCodeEditorProps {
  value: string;
  onChange: (text: string) => void;
  /** Accessible name of the text area. */
  label: string;
  rows?: number;
  disabled?: boolean;
  /** File name offered by Download; omit to hide the button. */
  downloadName?: string;
  /** Extra toolbar actions shown before the built-in ones. */
  actions?: ReactNode;
  /** Hide the syntax panel (the host shows its own). */
  quiet?: boolean;
}

/** Points at the problem with the offending line and a caret under the column. */
export function JsonProblem({
  problem,
  onLocate,
}: {
  problem: JsonSyntaxProblem;
  onLocate?: () => void;
}) {
  return (
    <div className="ps-json-problem" data-tone="danger">
      <div className="ps-json-problem-head">
        <AlertTriangle size={16} aria-hidden />
        <div>
          <strong>
            Line {problem.line}, column {problem.column}: {problem.message}
          </strong>
          <span>{problem.hint}</span>
        </div>
        {onLocate && (
          <button type="button" className="button" onClick={onLocate}>
            Show me
          </button>
        )}
      </div>
      <pre className="ps-json-excerpt" aria-label="Where the problem is">
        {problem.excerpt.map((row) => (
          <span
            key={row.line}
            className={row.line === problem.line ? "is-problem" : undefined}
          >
            <span className="ps-json-excerpt-line">{row.line}</span>
            {row.text || " "}
            {row.line === problem.line && (
              <span className="ps-json-caret" aria-hidden>
                {"\n"}
                <span className="ps-json-excerpt-line"> </span>
                {" ".repeat(Math.max(0, problem.column - 1))}^
              </span>
            )}
          </span>
        ))}
      </pre>
    </div>
  );
}

/**
 * A JSON text area with line numbers, a format button, copy, download and a
 * plain-language syntax panel that can jump straight to the problem.
 */
export function JsonCodeEditor({
  value,
  onChange,
  label,
  rows = 14,
  disabled = false,
  downloadName,
  actions,
  quiet = false,
}: JsonCodeEditorProps) {
  const id = useId(),
    area = useRef<HTMLTextAreaElement>(null),
    gutter = useRef<HTMLDivElement>(null),
    [copied, setCopied] = useState(false);
  const result = useMemo(
    () => (value.trim() ? parseJson(value) : undefined),
    [value],
  );
  const problem = result && !result.ok ? result.problem : undefined;
  const lines = Math.max(rows, value.split("\n").length);
  const locate = () => {
    const el = area.current;
    if (!el || !problem) return;
    el.focus();
    el.setSelectionRange(
      problem.offset,
      Math.min(value.length, problem.offset + 1),
    );
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.scrollTop = Math.max(0, (problem.line - 3) * lineHeight);
  };
  return (
    <div className="ps-json-editor" data-invalid={problem ? "" : undefined}>
      <div
        className="ps-json-toolbar"
        role="toolbar"
        aria-label={`${label} tools`}
      >
        <span className="ps-json-state" aria-hidden>
          {!value.trim() ? (
            "Empty"
          ) : problem ? (
            <>
              <AlertTriangle size={13} /> Needs a fix on line {problem.line}
            </>
          ) : (
            <>
              <Check size={13} /> Valid JSON
            </>
          )}
        </span>
        {actions}
        <button
          type="button"
          className="button"
          disabled={disabled || !result?.ok}
          onClick={() =>
            result?.ok && onChange(JSON.stringify(result.value, null, 2))
          }
        >
          <WandSparkles size={14} aria-hidden /> Format
        </button>
        <button
          type="button"
          className="button"
          disabled={!value}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            } catch {
              area.current?.select();
            }
          }}
        >
          {copied ? (
            <Check size={14} aria-hidden />
          ) : (
            <Copy size={14} aria-hidden />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
        {downloadName && (
          <button
            type="button"
            className="button"
            disabled={!value}
            onClick={() => {
              const url = URL.createObjectURL(
                new Blob([value], { type: "application/json" }),
              );
              const link = document.createElement("a");
              link.href = url;
              link.download = downloadName;
              link.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            <Download size={14} aria-hidden /> Download
          </button>
        )}
      </div>
      <div className="ps-json-surface">
        <div className="ps-json-gutter" ref={gutter} aria-hidden>
          {Array.from({ length: lines }, (_, i) => (
            <span
              key={i}
              className={
                problem && problem.line === i + 1 ? "is-problem" : undefined
              }
            >
              {i + 1}
            </span>
          ))}
        </div>
        <textarea
          ref={area}
          aria-label={label}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem && !quiet ? `${id}-problem` : undefined}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          wrap="off"
          rows={rows}
          disabled={disabled}
          value={value}
          onScroll={(e) => {
            if (gutter.current)
              gutter.current.scrollTop = e.currentTarget.scrollTop;
          }}
          onKeyDown={(e) => {
            // Tab indents instead of leaving the editor; Esc then Tab leaves.
            if (
              e.key !== "Tab" ||
              e.shiftKey ||
              e.altKey ||
              e.ctrlKey ||
              e.metaKey
            )
              return;
            const el = e.currentTarget;
            if (el.dataset.escaped) return;
            e.preventDefault();
            const { selectionStart: start, selectionEnd: end } = el;
            const next = value.slice(0, start) + "  " + value.slice(end);
            onChange(next);
            requestAnimationFrame(() =>
              el.setSelectionRange(start + 2, start + 2),
            );
          }}
          onKeyUp={(e) => {
            if (e.key === "Escape") e.currentTarget.dataset.escaped = "1";
          }}
          onBlur={(e) => delete e.currentTarget.dataset.escaped}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {problem && !quiet && (
        <div id={`${id}-problem`}>
          <JsonProblem problem={problem} onLocate={locate} />
        </div>
      )}
    </div>
  );
}
